import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Loader2, Home, User, LogOut, Bell, MessageSquare,
  CheckCircle, Clock, XCircle, Crown, Settings,
  ArrowRight, Inbox, CreditCard, MapPin, Zap, Eye, EyeOff,
  TrendingUp, Star, Award
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Provider Dashboard
 * Shows incoming requests, SMS wallet, and subscription status
 */
const ProviderDashboardPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapSettings, setMapSettings] = useState(null);
  const [mapTrialStatus, setMapTrialStatus] = useState(null);
  const [updatingMapVisibility, setUpdatingMapVisibility] = useState(false);

  useEffect(() => {
    fetchProviderData();
    fetchMapSettings();
    
    // Check for map subscription callback
    const mapSubResult = searchParams.get('map_subscription');
    const sessionId = searchParams.get('session_id');
    const upgrade = searchParams.get('upgrade');
    
    if (mapSubResult === 'success' && sessionId) {
      handleMapSubscriptionSuccess(sessionId);
    } else if (mapSubResult === 'cancelled') {
      toast.info('Map subscription was cancelled');
    }
    
    // Handle upgrade request from upsell banner
    if (upgrade === 'boost') {
      // Scroll to map visibility section and trigger boost purchase
      setTimeout(() => {
        const mapCard = document.querySelector('[data-testid="boost-upsell-card"]');
        if (mapCard) {
          mapCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          mapCard.classList.add('ring-2', 'ring-green-500');
          setTimeout(() => mapCard.classList.remove('ring-2', 'ring-green-500'), 2000);
        }
      }, 500);
      // Clear the URL param
      navigate('/provider/dashboard', { replace: true });
    }
  }, []);
  
  const fetchMapSettings = async () => {
    const sessionToken = localStorage.getItem('provider_session');
    if (!sessionToken) return;
    
    try {
      const response = await fetch(`${API_URL}/api/map/my/settings`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMapSettings(data.settings);
        setMapTrialStatus(data.trial);
      }
    } catch (error) {
      console.error('Error fetching map settings:', error);
    }
  };
  
  // Analytics tracking helper for boost upsell
  const trackAnalyticsEvent = async (eventType) => {
    try {
      await fetch(`${API_URL}/api/map/analytics?event_type=${eventType}`, {
        method: 'POST'
      });
    } catch (e) {
      // Silent fail
    }
  };
  
  const handleMapSubscriptionSuccess = async (sessionId) => {
    const sessionToken = localStorage.getItem('provider_session');
    
    try {
      const response = await fetch(`${API_URL}/api/map/subscription/status?session_id=${sessionId}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'active') {
          toast.success('Map visibility activated!');
          // Track analytics - payment_succeeded
          trackAnalyticsEvent('payment_succeeded');
          fetchMapSettings();
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
    
    // Clear URL params
    navigate('/provider/dashboard', { replace: true });
  };
  
  const toggleMapVisibility = async (enabled) => {
    const sessionToken = localStorage.getItem('provider_session');
    setUpdatingMapVisibility(true);
    
    try {
      const response = await fetch(`${API_URL}/api/map/my/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ map_visible: enabled })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMapSettings(data.settings);
        toast.success(enabled ? 'Now visible on map!' : 'Hidden from map');
      } else if (response.status === 402) {
        // Subscription required
        toast.error('Subscription required to appear on map');
        handleMapSubscribe('map_basic');
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating map visibility:', error);
      toast.error('Failed to update map visibility');
    } finally {
      setUpdatingMapVisibility(false);
    }
  };
  
  const handleMapSubscribe = async (planId = 'map_basic') => {
    const sessionToken = localStorage.getItem('provider_session');
    
    // Track analytics - checkout_opened
    trackAnalyticsEvent('checkout_opened');
    
    try {
      const response = await fetch(`${API_URL}/api/map/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan_id: planId })
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        // Note: stripe_mode is returned in response - always 'test' until live is enabled
        window.location.href = data.checkout_url;
      } else if (data.mode === 'manual') {
        toast.info('Contact admin to activate map visibility');
      } else {
        throw new Error('Failed to create checkout');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start subscription');
      trackAnalyticsEvent('payment_failed');
    }
  };
  
  const activateFoundersTrial = async () => {
    const sessionToken = localStorage.getItem('provider_session');
    
    try {
      const response = await fetch(`${API_URL}/api/map/my/trial/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ categories: provider.service_categories })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Founders trial activated! ${data.days_remaining} days free.`);
        fetchMapSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to activate trial');
      }
    } catch (error) {
      console.error('Error activating trial:', error);
      toast.error('Failed to activate trial');
    }
  };

  useEffect(() => {
    fetchProviderData();
  }, []);

  const fetchProviderData = async () => {
    const sessionToken = localStorage.getItem('provider_session');
    
    if (!sessionToken) {
      navigate('/provider/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/providers/me`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('provider_session');
          navigate('/provider/login');
          return;
        }
        throw new Error('Failed to fetch provider data');
      }

      const data = await response.json();
      setProvider(data);
      
      // Fetch requests (simplified for MVP)
      // In production, this would be paginated
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const sessionToken = localStorage.getItem('provider_session');
    
    try {
      await fetch(`${API_URL}/api/providers/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` },
        credentials: 'include'
      });
    } catch (e) {
      // Ignore logout errors
    }
    
    localStorage.removeItem('provider_session');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600 mb-4">Please sign in to access your dashboard</p>
            <Button asChild>
              <Link to="/provider/login">Sign In</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const wallet = provider.sms_wallet || {};
  const subscription = provider.subscription;
  const quotaRemaining = Math.max(0, (wallet.monthly_quota || 0) - (wallet.monthly_used || 0));
  const totalCredits = quotaRemaining + (wallet.balance || 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-orange-600">KEIKKA</Link>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="hidden sm:inline-flex">
              Provider
            </Badge>
            <Button variant="ghost" size="icon" asChild>
              <Link to="/settings">
                <Settings className="h-5 w-5" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Quick Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-2">
          <div className="flex gap-2 overflow-x-auto">
            <Button variant="ghost" size="sm" className="text-orange-600 bg-orange-50" asChild>
              <Link to="/provider/dashboard">
                <Home className="h-4 w-4 mr-1" />
                Dashboard
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/provider/map-visibility">
                <MapPin className="h-4 w-4 mr-1" />
                Map Visibility
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/settings">
                <Settings className="h-4 w-4 mr-1" />
                Settings
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome, {provider.name}
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your services and respond to requests
          </p>
        </div>

        {/* PROMINENT: Live Map Visibility Card */}
        <Card className="mb-6 border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="map-visibility-prominent-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <MapPin className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('mapVisibility.title') || 'Live Map Visibility'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {t('mapVisibility.subtitle') || 'Näita oma ettevõtet avalikul kaardil'}
                  </p>
                  <div className="flex items-center gap-2">
                    {/* Status Badge */}
                    {mapSettings?.subscription_status === 'trial' ? (
                      <Badge className="bg-purple-100 text-purple-700">
                        {t('mapVisibility.statusTrial') || 'TASUTA PROOVIAEG'} 
                        {mapTrialStatus?.days_remaining > 0 && ` (${mapTrialStatus.days_remaining} ${t('mapVisibility.daysLeft') || 'päeva jäänud'})`}
                      </Badge>
                    ) : mapSettings?.map_tier === 'boost' ? (
                      <Badge className="bg-green-100 text-green-700">
                        <Zap className="h-3 w-3 mr-1" />
                        {t('mapVisibility.statusBoost') || 'NÄHTAV (Boost)'}
                      </Badge>
                    ) : mapSettings?.subscription_status === 'active' && mapSettings?.map_visible ? (
                      <Badge className="bg-blue-100 text-blue-700">
                        <Eye className="h-3 w-3 mr-1" />
                        {t('mapVisibility.statusVisible') || 'NÄHTAV (Basic)'}
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700">
                        <EyeOff className="h-3 w-3 mr-1" />
                        {t('mapVisibility.statusHidden') || 'PEIDETUD'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button asChild className="bg-orange-600 hover:bg-orange-700">
                  <Link to="/provider/map-visibility">
                    <Settings className="h-4 w-4 mr-2" />
                    {t('mapVisibility.openSettings') || 'Ava seaded'}
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/map">
                    <MapPin className="h-4 w-4 mr-2" />
                    {t('mapVisibility.viewOnMap') || 'Vaata kaardil'}
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Profile Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5" />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{provider.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{provider.phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="font-medium">{provider.city}, {provider.country}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Services</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {provider.service_categories?.map((cat) => (
                    <Badge key={cat} variant="secondary" className="text-xs">
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="pt-2">
                <Badge className={provider.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                  {provider.status === 'approved' ? 'Active' : 'Pending Approval'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* SMS Wallet Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageSquare className="h-5 w-5" />
                SMS Wallet
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-3xl font-bold text-orange-600">
                  {totalCredits}
                </div>
                <p className="text-sm text-gray-500">Credits Available</p>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="p-2 bg-blue-50 rounded">
                  <div className="font-semibold text-blue-700">{quotaRemaining}</div>
                  <div className="text-xs text-gray-500">Monthly Quota</div>
                </div>
                <div className="p-2 bg-green-50 rounded">
                  <div className="font-semibold text-green-700">{wallet.balance || 0}</div>
                  <div className="text-xs text-gray-500">Purchased</div>
                </div>
              </div>

              {totalCredits < 5 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  Low credits! Top up to keep receiving SMS notifications.
                </div>
              )}

              <Button className="w-full" variant="outline">
                <CreditCard className="h-4 w-4 mr-2" />
                Buy SMS Credits
              </Button>
            </CardContent>
          </Card>

          {/* Subscription Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Crown className="h-5 w-5" />
                Subscription
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {subscription?.status === 'active' ? (
                <>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                    <Badge className="bg-yellow-100 text-yellow-700">
                      {subscription.plan_id?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    You have access to Quick Response and priority matching.
                  </p>
                  {subscription.current_period_end && (
                    <p className="text-xs text-gray-500">
                      Renews: {new Date(subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-gray-600 text-sm">
                    Upgrade to get instant SMS notifications and Quick Response access.
                  </p>
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      From €4.99<span className="text-sm font-normal">/mo</span>
                    </div>
                    <ul className="text-xs text-gray-600 mt-2 space-y-1">
                      <li>• Instant SMS notifications</li>
                      <li>• Quick Response access</li>
                      <li>• Priority matching</li>
                    </ul>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-orange-500 to-yellow-500">
                    <Crown className="h-4 w-4 mr-2" />
                    Upgrade Now
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Map Visibility Card */}
          <Card className="col-span-full md:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5" />
                Live Map Visibility
              </CardTitle>
              <CardDescription>
                Appear on the public service map
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {mapSettings?.map_visible ? (
                    <Eye className="h-5 w-5 text-green-600" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <p className="font-medium">
                      {mapSettings?.map_visible ? 'Visible on Map' : 'Hidden from Map'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {mapSettings?.subscription_status === 'trial' 
                        ? `Founders Trial - ${mapTrialStatus?.days_remaining || 0} days left`
                        : mapSettings?.subscription_status === 'active'
                          ? `${(mapSettings?.map_tier || 'basic').toUpperCase()} Plan`
                          : 'No subscription'}
                    </p>
                  </div>
                </div>
                
                {/* Toggle - only if has subscription */}
                {(mapSettings?.subscription_status === 'active' || mapSettings?.subscription_status === 'trial') && (
                  <Switch
                    checked={mapSettings?.map_visible || false}
                    onCheckedChange={toggleMapVisibility}
                    disabled={updatingMapVisibility}
                    data-testid="map-visibility-toggle"
                  />
                )}
              </div>
              
              {/* Tier Badge */}
              {mapSettings?.subscription_status !== 'inactive' && (
                <div className="flex items-center gap-2">
                  <Badge className={
                    mapSettings?.map_tier === 'premium' 
                      ? 'bg-yellow-100 text-yellow-800'
                      : mapSettings?.map_tier === 'boost'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                  }>
                    {mapSettings?.map_tier === 'premium' && <Zap className="h-3 w-3 mr-1" />}
                    {(mapSettings?.map_tier || 'basic').toUpperCase()}
                  </Badge>
                  {mapSettings?.map_visible && (
                    <Button variant="link" size="sm" className="text-xs" asChild>
                      <Link to="/map">View on Map →</Link>
                    </Button>
                  )}
                </div>
              )}
              
              {/* Actions based on status */}
              {mapSettings?.subscription_status === 'inactive' && (
                <>
                  {/* Check if founders trial available */}
                  {!mapTrialStatus?.has_trial && (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-blue-500 text-white">FREE TRIAL</Badge>
                        <span className="text-sm font-medium">60 Days</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        Be one of the first 500 providers to get free map visibility.
                      </p>
                      <Button 
                        onClick={activateFoundersTrial}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        Activate Founders Trial
                      </Button>
                    </div>
                  )}
                  
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg">
                    <div className="text-xl font-bold text-orange-600">
                      From €4.99<span className="text-sm font-normal">/mo</span>
                    </div>
                    <ul className="text-xs text-gray-600 mt-2 space-y-1">
                      <li>• Visible on public map</li>
                      <li>• Clickable profile pin</li>
                      <li>• Contact details shown</li>
                    </ul>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleMapSubscribe('map_basic')}
                      className="flex-1"
                      variant="outline"
                      size="sm"
                    >
                      Starter €4.99
                    </Button>
                    <Button 
                      onClick={() => handleMapSubscribe('map_boost')}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      <Zap className="h-3 w-3 mr-1" />
                      Boost €9.99
                    </Button>
                  </div>
                </>
              )}
              
              {/* Upgrade options for active users - Enhanced Upsell Card */}
              {/* Show for BASIC tier providers - both inactive and active basic */}
              {(mapSettings?.subscription_status === 'inactive' || 
                (mapSettings?.subscription_status === 'active' && mapSettings?.map_tier === 'basic')) && (
                <BoostUpsellCard 
                  t={t}
                  onBoostClick={() => {
                    trackAnalyticsEvent('boost_clicked');
                    handleMapSubscribe('map_boost');
                  }}
                  trackAnalyticsEvent={trackAnalyticsEvent}
                />
              )}
              
              {/* Trial ending warning */}
              {mapSettings?.subscription_status === 'trial' && mapTrialStatus?.days_remaining <= 14 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="text-amber-800 font-medium">
                    Trial ending in {mapTrialStatus.days_remaining} days
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Subscribe now to keep your map visibility.
                  </p>
                  <Button 
                    onClick={() => handleMapSubscribe('map_basic')}
                    className="w-full mt-2"
                    size="sm"
                  >
                    Subscribe Now
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Incoming Requests */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="h-5 w-5" />
              Incoming Requests
            </CardTitle>
            <CardDescription>
              Service requests matching your categories
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(!provider.recent_requests || provider.recent_requests.length === 0) ? (
              <div className="text-center py-12 text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No requests yet</p>
                <p className="text-sm">
                  You'll receive notifications when clients need services in your area.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {provider.recent_requests.map((req) => (
                  <div 
                    key={req.notification_id || req.booking_id} 
                    className={`p-4 border-2 rounded-lg transition-colors ${
                      req.is_priority 
                        ? 'border-orange-300 bg-orange-50' 
                        : 'border-gray-200 hover:border-orange-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          {req.is_priority && (
                            <Badge className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white">
                              <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z"/>
                              </svg>
                              PRIORITY
                            </Badge>
                          )}
                          <h4 className="font-medium">{req.category || 'Service Request'}</h4>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {req.city}, {req.country} • {req.preferred_date}
                        </p>
                        {req.sla_deadline && (
                          <p className="text-xs text-gray-400 mt-1">
                            Respond by: {new Date(req.sla_deadline).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <Badge className={
                        req.status === 'replied' ? 'bg-green-100 text-green-700' :
                        req.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {req.status}
                      </Badge>
                    </div>
                    {req.response_token && req.status !== 'replied' && (
                      <Button 
                        size="sm" 
                        className="mt-3"
                        onClick={() => window.open(`/provider/respond/${req.booking_id}?token=${req.response_token}`, '_blank')}
                      >
                        Respond Now
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

/**
 * Boost Upsell Card Component
 * Shows for BASIC providers to encourage upgrade to Boost tier
 * Tracks boost_prompt_viewed on mount
 */
const BoostUpsellCard = ({ t, onBoostClick, trackAnalyticsEvent }) => {
  useEffect(() => {
    // Track that the upsell was viewed
    trackAnalyticsEvent('boost_prompt_viewed');
  }, []);

  return (
    <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg" data-testid="boost-upsell-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="bg-green-100 rounded-full p-2">
          <TrendingUp className="h-4 w-4 text-green-600" />
        </div>
        <div>
          <h4 className="font-semibold text-green-800 text-sm">
            {t('upsell.dashboardTitle') || 'Boost Your Map Listing'}
          </h4>
          <p className="text-xs text-green-700 mt-1">
            {t('upsell.dashboardBody') || 'Stand out from the competition with priority placement.'}
          </p>
        </div>
      </div>
      <ul className="text-xs text-green-700 space-y-1 mb-3 ml-11">
        <li className="flex items-center gap-1">
          <Star className="h-3 w-3" />
          {t('upsell.features.priority') || 'Priority placement'}
        </li>
        <li className="flex items-center gap-1">
          <Award className="h-3 w-3" />
          {t('upsell.features.badge') || 'Featured badge'}
        </li>
        <li className="flex items-center gap-1">
          <TrendingUp className="h-3 w-3" />
          {t('upsell.features.visibility') || '3x more visibility'}
        </li>
      </ul>
      <Button 
        onClick={onBoostClick}
        className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
        size="sm"
        data-testid="boost-upgrade-btn"
      >
        <Zap className="h-4 w-4 mr-2" />
        {t('upsell.upgradeCta') || 'Upgrade to Boost'} - {t('upsell.price') || '€9.99/mo'}
      </Button>
    </div>
  );
};

export default ProviderDashboardPage;
